/**
 * الشِل — سايدبار + شريط علوي + محتوى + زر المساعد.
 * كل شاشة جوّه السيستم بتتلف بيه (عدا شاشة الدخول).
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Sidebar, type ConnHealth } from "./Sidebar";
import { TopBar } from "./TopBar";
import { AssistantFab } from "./AssistantFab";
import { useSession } from "@/lib/session";
import { useRouter } from "@/lib/router";

const COLLAPSE_KEY = "rtl.side.collapsed";

/** العرض اللي تحته السايدبار بيبقى مطويًا افتراضيًا — نفس نقطة الكسر في shell.css */
const MOBILE_W = 720;

/**
 * 🔴 **ده المكان اللي حالة الـCSS بتتوصل فيه بحالة الـJS.**
 *
 * الـmedia query كانت بتطوي السايدبار بصريًا على الشاشات الصغيرة و`collapsed`
 * في React بتفضل `false` — فالكلاس `mini` ما بيتحطش، و`Sidebar` بيحسب
 * `title` من الحالة الغلط، والنتيجة 15 أيقونة بلا اسم وبلا تلميح.
 *
 * ⚠️ الاختيار الصريح للموظف بيفضل **محترم على كل المقاسات** — اللي بيتغيّر
 *    هو الافتراضي بس لما مفيش قيمة محفوظة.
 */
function readCollapsed(): boolean {
  try {
    // 🔴 **عرض الشاشة بيغلب التفضيل المحفوظ — مش العكس.**
    //    الفريق شغّال على السيستم من شهر، يعني كل متصفح عنده
    //    `rtl.side.collapsed="0"` متخزّن من جلسة على اللابتوب. لو قرينا
    //    المحفوظ الأول، المشرفة تفتح على موبايل ٣٩٠ بكسل فتلاقي السايدبار
    //    مفتوح واخد ٢٤٨ بكسل — يفضل ١٤٢ للمحتوى كله.
    // ⚠️ والتفضيل ده **تفضيل لابتوب**، مش تفضيل للموبايل: الشاشة الضيقة
    //    سياق تاني، ومعندناش تفضيل محفوظ ليها أصلًا.
    if (window.innerWidth <= MOBILE_W) return true;
    const saved = window.localStorage.getItem(COLLAPSE_KEY);
    if (saved === null) return false;
    return saved === "1";
  } catch {
    return false;
  }
}

export type ShellProps = {
  title: string;
  crumb?: ReactNode;
  search?: {
    value: string;
    onChange: (v: string) => void;
    onSubmit?: (v: string) => void;
    placeholder?: string;
  };
  children: ReactNode;
  onAssistantClick?: () => void;
  health?: { telegram: ConnHealth; leadForm: ConnHealth };
};

export function Shell({ title, crumb, search, children, onAssistantClick, health }: ShellProps) {
  const { me, signOut } = useSession();
  const { navigate, path, search: routerSearch } = useRouter();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  /** زر «اسأل المساعد» بياخد معاه الشاشة اللي الموظف واقف فيها — المساعد يعرف
   *  إنه فاتح رحلة أو طلب بعينه من غير ما يسأله «أنهي أسرة؟» */
  const openAssistant = useCallback(() => {
    if (path.startsWith("/assistant")) return;
    navigate(`/assistant?from=${encodeURIComponent(path + routerSearch)}`);
  }, [navigate, path, routerSearch]);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* التخزين المحلي ممنوع أحيانًا — الطي بيفضل شغّال للجلسة الحالية */
    }
  }, [collapsed]);

  const handleSignOut = useCallback(() => {
    void signOut().finally(() => navigate("/login", { replace: true }));
  }, [signOut, navigate]);

  const user = me?.user;

  return (
    <div className="app">
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        counts={{ desk: me?.overdue_tasks ?? 0 }}
        scopeLabel={
          user?.role === "director" ? "بتشوف كل الأقسام" : "بتشوف أقسامك بس"
        }
        {...(health ? { health } : {})}
      />

      <main className="body">
        <TopBar
          title={title}
          {...(crumb ? { crumb } : {})}
          {...(search ? { search } : {})}
          unreadCount={me?.unread_notifications ?? 0}
          onBellClick={() => navigate("/notifications")}
          {...(user
            ? {
                user: {
                  name: user.full_name_ar,
                  roleTitle: user.role_title_ar ?? user.department_name_ar ?? null,
                },
              }
            : {})}
          onSignOut={handleSignOut}
        />
        <div className="content">{children}</div>
      </main>

      <AssistantFab onClick={onAssistantClick ?? openAssistant} />
    </div>
  );
}
